/**
 * Pure helpers for reading, validating, and serializing shareable query params.
 * Never throw on malformed input — coerce to safe defaults instead.
 */

/**
 * @param {unknown} raw
 * @param {import('./search-param-schemas.js').ParamSchema} schema
 * @returns {{ value: *, usedDefault: boolean, invalid: boolean }}
 */
export function coerceParam(raw, schema) {
  if (!schema || typeof schema !== 'object') {
    return { value: raw == null ? null : String(raw), usedDefault: false, invalid: false };
  }

  const def = Object.prototype.hasOwnProperty.call(schema, 'defaultValue')
    ? schema.defaultValue
    : null;

  if (raw == null || raw === '') {
    return { value: def, usedDefault: true, invalid: false };
  }

  const type = schema.type || 'string';

  if (type === 'string') {
    let s = String(raw);
    if (schema.maxLength && s.length > schema.maxLength) {
      s = s.slice(0, schema.maxLength);
      return { value: s, usedDefault: false, invalid: true };
    }
    return { value: s, usedDefault: false, invalid: false };
  }

  if (type === 'id') {
    let s = String(raw).trim();
    if (!s) return { value: def, usedDefault: true, invalid: false };
    if (schema.maxLength && s.length > schema.maxLength) {
      return { value: def, usedDefault: true, invalid: true };
    }
    // Reject obvious sensitive payloads / serialized objects
    if (s.includes('{') || s.includes('%7B') || s.length > (schema.maxLength || 64)) {
      return { value: def, usedDefault: true, invalid: true };
    }
    return { value: s, usedDefault: false, invalid: false };
  }

  if (type === 'enum') {
    const s = String(raw);
    const values = Array.isArray(schema.values) ? schema.values : [];
    if (values.includes(s)) {
      return { value: s, usedDefault: false, invalid: false };
    }
    return { value: def, usedDefault: true, invalid: true };
  }

  if (type === 'int') {
    const trimmed = String(raw).trim();
    if (!/^-?\d+$/.test(trimmed)) {
      return { value: def, usedDefault: true, invalid: true };
    }
    let v = Number.parseInt(trimmed, 10);
    if (!Number.isFinite(v)) {
      return { value: def, usedDefault: true, invalid: true };
    }
    let invalid = false;
    if (schema.min != null && v < schema.min) {
      v = schema.min;
      invalid = true;
    }
    if (schema.max != null && v > schema.max) {
      v = schema.max;
      invalid = true;
    }
    return { value: v, usedDefault: v === def, invalid };
  }

  if (type === 'boolean') {
    const s = String(raw).toLowerCase();
    if (s === '1' || s === 'true' || s === 'yes') {
      return { value: true, usedDefault: false, invalid: false };
    }
    if (s === '0' || s === 'false' || s === 'no') {
      return { value: false, usedDefault: false, invalid: false };
    }
    return { value: def, usedDefault: true, invalid: true };
  }

  if (type === 'period') {
    return coerceParam(raw, {
      type: 'enum',
      defaultValue: def,
      values: schema.values || ['24h', '7d', '30d', '90d', 'ytd', 'all'],
    });
  }

  if (type === 'list') {
    const parts = String(raw)
      .split(',')
      .map((p) => p.trim())
      .filter(Boolean);
    if (!parts.length) return { value: def || [], usedDefault: true, invalid: false };
    if (schema.maxLength && parts.join(',').length > schema.maxLength) {
      return { value: def || [], usedDefault: true, invalid: true };
    }
    return { value: parts, usedDefault: false, invalid: false };
  }

  return { value: String(raw), usedDefault: false, invalid: false };
}

/**
 * Parse URLSearchParams (or a plain object / string) against a schema.
 * @returns {{ values: Record<string, *>, invalidKeys: string[], needsCanonicalize: boolean }}
 */
export function parseSearchParams(input, schema = {}) {
  const sp = toURLSearchParams(input);
  const values = {};
  const invalidKeys = [];
  let needsCanonicalize = false;

  for (const [key, def] of Object.entries(schema || {})) {
    // Prefer first value when duplicated
    const raw = sp.get(key);
    const all = sp.getAll(key);
    if (all.length > 1) needsCanonicalize = true;

    const { value, usedDefault, invalid } = coerceParam(raw, def);
    values[key] = value;
    if (invalid) {
      invalidKeys.push(key);
      needsCanonicalize = true;
    }
    // Default present in URL → strip on canonicalize
    if (usedDefault && raw != null && raw !== '') {
      needsCanonicalize = true;
    }
    if (!usedDefault && isDefaultValue(value, def) && raw != null) {
      needsCanonicalize = true;
    }
  }

  return { values, invalidKeys, needsCanonicalize };
}

function isDefaultValue(value, def) {
  if (!def) return false;
  const d = def.defaultValue;
  if (Array.isArray(d) && Array.isArray(value)) {
    return d.length === value.length && d.every((x, i) => x === value[i]);
  }
  return value === d;
}

/**
 * Serialize schema values into a URLSearchParams, omitting defaults.
 * Preserves unrelated keys from `existing` when provided.
 */
export function serializeSearchParams(values, schema = {}, existing = null) {
  const out = new URLSearchParams();
  const existingSp = existing != null ? toURLSearchParams(existing) : null;

  if (existingSp) {
    for (const [key, val] of existingSp.entries()) {
      if (Object.prototype.hasOwnProperty.call(schema, key)) continue;
      out.append(key, val);
    }
  }

  for (const [key, def] of Object.entries(schema || {})) {
    if (!Object.prototype.hasOwnProperty.call(values || {}, key)) continue;
    const value = values[key];
    if (isDefaultValue(value, def)) continue;
    if (value == null || value === '') continue;
    if (typeof value === 'boolean') {
      if (value === false && def.defaultValue === false) continue;
      out.set(key, value ? '1' : '0');
      continue;
    }
    if (Array.isArray(value)) {
      if (!value.length) continue;
      out.set(key, value.join(','));
      continue;
    }
    out.set(key, String(value));
  }

  return out;
}

/**
 * Patch values onto current search string. Resets page to default when
 * non-page filters change (unless `keepPage` is true).
 */
export function updateSearchParams(current, schema, patch, { keepPage = false } = {}) {
  const { values } = parseSearchParams(current, schema);
  const next = { ...values, ...(patch || {}) };

  const patchKeys = Object.keys(patch || {});
  const touchesFilters = patchKeys.some((k) => k !== 'page');
  if (touchesFilters && !keepPage && schema.page) {
    next.page = schema.page.defaultValue ?? 1;
  }

  return serializeSearchParams(next, schema, current);
}

export function resetSearchParams(schema, existing = null) {
  const defaults = {};
  for (const [key, def] of Object.entries(schema || {})) {
    defaults[key] = def.defaultValue;
  }
  return serializeSearchParams(defaults, schema, existing);
}

export function searchParamsToString(sp) {
  if (!sp) return '';
  const s = typeof sp.toString === 'function' ? sp.toString() : String(sp);
  return s ? `?${s}` : '';
}

export function toURLSearchParams(input) {
  if (input instanceof URLSearchParams) return input;
  if (typeof input === 'string') {
    const s = input.startsWith('?') ? input.slice(1) : input;
    return new URLSearchParams(s);
  }
  if (input && typeof input === 'object') {
    // React Router's ReadonlyURLSearchParams or plain object
    if (typeof input.get === 'function' && typeof input.entries === 'function') {
      return new URLSearchParams(Array.from(input.entries()));
    }
    const sp = new URLSearchParams();
    for (const [k, v] of Object.entries(input)) {
      if (v == null || v === '') continue;
      if (Array.isArray(v)) sp.set(k, v.join(','));
      else if (typeof v === 'boolean') sp.set(k, v ? '1' : '0');
      else sp.set(k, String(v));
    }
    return sp;
  }
  return new URLSearchParams();
}

/**
 * Compare two search strings for semantic equality (order-insensitive for schema keys).
 */
export function searchParamsEqual(a, b) {
  const spa = toURLSearchParams(a);
  const spb = toURLSearchParams(b);
  const ka = [...new Set(spa.keys())].sort();
  const kb = [...new Set(spb.keys())].sort();
  if (ka.length !== kb.length) return false;
  for (let i = 0; i < ka.length; i += 1) {
    if (ka[i] !== kb[i]) return false;
    if (spa.get(ka[i]) !== spb.get(kb[i])) return false;
  }
  return true;
}
